import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';

interface Stats {
  totalUsers: number;
  totalClaims: number;
  totalAllocated: string;
  totalClaimed: string;
}


interface SystemHealth {
  status: string;
  timestamp: string;
  uptime: number;
  environment: string;
  version: string;
}

export const Dashboard = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Smart API URL detection
        const getApiUrl = () => {
          if (process.env.NODE_ENV === 'production') {
            return process.env.REACT_APP_API_URL || 'https://atleta-claiming-be-production.up.railway.app/api';
          }
          return 'http://localhost:3000/api';
        };
        
        const API_URL = getApiUrl();
        
        // Fetch stats
        const statsResponse = await fetch(`${API_URL}/airdrop/stats`);
        const statsData = await statsResponse.json();
        setStats(statsData.data);


        // Fetch health
        const healthResponse = await fetch(`${API_URL}/health`);
        const healthData = await healthResponse.json();
        setHealth(healthData);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const formatTokenAmount = (amount: string) => {
    try {
      // API already returns human-readable ATLA values, just add formatting
      const numericAmount = parseFloat(amount);
      return numericAmount.toLocaleString() + ' ATLA';
    } catch {
      return '0 ATLA';
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        🎯 Atleta Network Airdrop Dashboard
      </Typography>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 2 }}>
        <Card sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Users
            </Typography>
            <Typography variant="h4">
              {stats?.totalUsers || 0}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Claims
            </Typography>
            <Typography variant="h4">
              {stats?.totalClaims || 0}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Allocated
            </Typography>
            <Typography variant="h6">
              {formatTokenAmount(stats?.totalAllocated || '0')}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Claimed
            </Typography>
            <Typography variant="h6">
              {formatTokenAmount(stats?.totalClaimed || '0')}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* System Status */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 3 }}>
        <Card sx={{ flex: '1 1 400px' }}>
          <CardHeader title="🔧 System Status" />
          <CardContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Database</Typography>
                <Chip label="Healthy" color="success" size="small" />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Blockchain</Typography>
                <Chip label="Connected" color="success" size="small" />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Indexer</Typography>
                <Chip label="Running" color="success" size="small" />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 400px' }}>
          <CardHeader title="📊 System Metrics" />
          <CardContent>
            {health && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Typography>
                    <Chip 
                      label={health.status} 
                      color={health.status === 'ok' ? 'success' : 'error'} 
                      size="small" 
                    />
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Uptime
                  </Typography>
                  <Typography>
                    {(health.uptime / 3600).toFixed(2)} hours
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Environment
                  </Typography>
                  <Typography>{health.environment}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Version
                  </Typography>
                  <Typography>{health.version}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Last Updated
                  </Typography>
                  <Typography>
                    {new Date(health.timestamp).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Quick Links */}
      <Card sx={{ marginTop: 3 }}>
        <CardHeader title="⚡ Quick Links" />
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Chip
              label="Swagger API Docs"
              onClick={() => {
                const apiUrl = process.env.NODE_ENV === 'production' 
                  ? (process.env.REACT_APP_API_URL || 'https://atleta-claiming-be-production.up.railway.app/api')
                  : 'http://localhost:3000/api';
                window.open(`${apiUrl}/docs`, '_blank');
              }}
              clickable
              color="primary"
            />
            <Chip
              label="Health Check"
              onClick={() => {
                const apiUrl = process.env.NODE_ENV === 'production' 
                  ? (process.env.REACT_APP_API_URL || 'https://atleta-claiming-be-production.up.railway.app/api')
                  : 'http://localhost:3000/api';
                window.open(`${apiUrl}/health`, '_blank');
              }}
              clickable
              color="secondary"
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};